import api from "./apiClient";
import { getPendingPayments } from "./walletService";
import { decodeBase64Json } from "../utils/decodeBase64Json";

/**
 * Decode API response.
 *
 * Supported response formats:
 *
 * [
 *   {
 *     ResultBase64: "..."
 *   }
 * ]
 *
 * or:
 *
 * {
 *   ResultBase64: "..."
 * }
 *
 * or direct JSON/object response.
 */
function decodeApiResponse(response) {
  const payload = response?.data;

  const resultBase64 =
    payload?.[0]?.ResultBase64 ??
    payload?.ResultBase64 ??
    payload?.[0]?.RESULT_BASE64 ??
    payload?.RESULT_BASE64 ??
    payload?.[0]?.resultBase64 ??
    payload?.resultBase64 ??
    null;

  if (resultBase64) {
    try {
      return decodeBase64Json(resultBase64);
    } catch (error) {
      console.error("Billing API: failed to decode ResultBase64", error);
      return null;
    }
  }

  // Direct array response
  if (Array.isArray(payload)) {
    return payload[0] ?? payload;
  }

  // Direct object response
  if (payload && typeof payload === "object") {
    return payload;
  }

  console.warn("Billing API: ResultBase64 not found", response);
  return null;
}

// -----------------------------------------------------------------------------
// HELPERS
// -----------------------------------------------------------------------------

const toNumber = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const normalizedValue =
    typeof value === "string"
      ? value.replace(/,/g, "").trim()
      : value;

  const number = Number(normalizedValue);

  return Number.isFinite(number) ? number : fallback;
};

const firstDefined = (...values) => {
  return values.find(
    (value) =>
      value !== undefined &&
      value !== null &&
      value !== "",
  );
};

const getResult = (decoded) => {
  if (!decoded) {
    return null;
  }

  return (
    decoded.result ??
    decoded.RESULT ??
    decoded.data ??
    decoded.DATA ??
    decoded.rows ??
    decoded.ROWS ??
    decoded
  );
};

const getArrayResult = (decoded) => {
  const result = getResult(decoded);

  if (Array.isArray(result)) {
    return result;
  }

  if (result && typeof result === "object") {
    return (
      result.rows ??
      result.ROWS ??
      result.data ??
      result.DATA ??
      result.items ??
      result.ITEMS ??
      result.results ??
      result.RESULTS ??
      []
    );
  }

  return [];
};

const normalizeDocumentType = (value, fallback = "invoice") => {
  const raw = String(value ?? fallback)
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");

  if (
    raw.includes("proforma") ||
    raw.includes("proformainvoice")
  ) {
    return "proforma";
  }

  if (
    raw.includes("invoice") ||
    raw.includes("factor")
  ) {
    return "invoice";
  }

  if (
    raw.includes("payment") ||
    raw.includes("transaction")
  ) {
    return "payment";
  }

  return raw || fallback;
};

const normalizeStatus = (value, type = "invoice") => {
  if (value === undefined || value === null || value === "") {
    return type === "proforma" ? "unpaid" : "open";
  }

  const raw = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]/g, "_");

  const statusMap = {
    "0": "draft",
    "1": "open",
    "2": "paid",
    "3": "cancelled",
    "4": "overdue",
    "5": "unpaid",

    draft: "draft",
    open: "open",
    unpaid: "unpaid",
    paid: "paid",
    overdue: "overdue",

    cancelled: "cancelled",
    canceled: "cancelled",

    pending: "pending",
    processing: "processing",
    failed: "failed",
    expired: "expired",

    partiallypaid: "partiallyPaid",
    partially_paid: "partiallyPaid",
    partiallypaidinvoice: "partiallyPaid",
  };

  return statusMap[raw] || raw;
};

// -----------------------------------------------------------------------------
// INVOICE ITEMS
// -----------------------------------------------------------------------------

const normalizeInvoiceItem = (item = {}) => {
  const quantity = toNumber(
    firstDefined(
      item.QUANTITY,
      item.QTY,
      item.quantity,
      item.qty,
      0,
    ),
  );

  const unitPrice = toNumber(
    firstDefined(
      item.UNIT_PRICE,
      item.UNIT_AMOUNT,
      item.PRICE,
      item.AMOUNT,
      item.unitPrice,
      item.unit_amount,
      item.price,
      item.amount,
      0,
    ),
  );

  const total = toNumber(
    firstDefined(
      item.LINE_TOTAL,
      item.LINE_AMOUNT,
      item.TOTAL_PRICE,
      item.TOTAL_AMOUNT,
      item.TOTAL,
      item.total,
      item.lineTotal,
      item.lineAmount,
      quantity * unitPrice,
    ),
  );

  return {
    id: firstDefined(
      item.INVOICE_DETAILS_ID,
      item.INVOICE_DETAIL_ID,
      item.DETAIL_ID,
      item.id,
      item.invoiceDetailsId,
      null,
    ),

    parameterId: firstDefined(
      item.PARAMETER_ID,
      item.LT_PARAMETER_ID,
      item.parameterId,
      item.parameter_id,
      null,
    ),

    description: firstDefined(
      item.PARAMETER_NAME,
      item.PARAMETER_TITLE,
      item.DESCRIPTION,
      item.ITEM_NAME,
      item.NAME,
      item.description,
      item.parameterName,
      item.itemName,
      item.name,
      "—",
    ),

    quantity,
    unitPrice,
    total,

    currency: firstDefined(
      item.CODE,
      item.CURRENCY,
      item.currency,
      "USD",
    ),

    raw: item,
  };
};

// -----------------------------------------------------------------------------
// COMMON DOCUMENT NORMALIZER
// -----------------------------------------------------------------------------

const normalizeDocument = (
  document = {},
  forcedType = null,
) => {
  const type = normalizeDocumentType(
    firstDefined(
      forcedType,
      document.type,
      document.TYPE,
      document.DOCUMENT_TYPE,
      document.documentType,
    ),
    "invoice",
  );

  const rawItems = firstDefined(
    document.ITEMS,
    document.items,
    document.INVOICE_ITEMS,
    document.invoiceItems,
    document.DETAILS,
    document.details,
    [],
  );

  const items = Array.isArray(rawItems)
    ? rawItems.map(normalizeInvoiceItem)
    : [];

  const subtotal = toNumber(
    firstDefined(
      document.SUB_TOTAL,
      document.SUBTOTAL,
      document.SUB_TOTAL_AMOUNT,
      document.SUBTOTAL_AMOUNT,
      document.subtotal,
      0,
    ),
  );

  const discountAmount = toNumber(
    firstDefined(
      document.DISCOUNT_AMOUNT,
      document.DISCOUNT,
      document.discountAmount,
      document.discount,
      0,
    ),
  );

  const discountPercent = toNumber(
    firstDefined(
      document.DISCOUNT_PERCENT,
      document.DISCOUNT_PERCENTAGE,
      document.discountPercent,
      document.discountPercentage,
      0,
    ),
  );

  const taxAmount = toNumber(
    firstDefined(
      document.TAX_AMOUNT,
      document.TAX,
      document.VAT_AMOUNT,
      document.taxAmount,
      document.tax,
      document.vatAmount,
      0,
    ),
  );

  const taxPercent = toNumber(
    firstDefined(
      document.TAX_PERCENT,
      document.TAX_PERCENTAGE,
      document.VAT_PERCENT,
      document.taxPercent,
      document.taxPercentage,
      document.vatPercent,
      0,
    ),
  );

  const calculatedTotal =
    subtotal - discountAmount + taxAmount;

  const total = toNumber(
    firstDefined(
      document.TOTAL_AMOUNT,
      document.GRAND_TOTAL,
      document.NET_TOTAL,
      document.TOTAL,
      document.totalAmount,
      document.total,
      calculatedTotal,
    ),
  );

  const paid = toNumber(
    firstDefined(
      document.PAID_AMOUNT,
      document.TOTAL_PAID,
      document.PAID,
      document.paidAmount,
      document.paid,
      0,
    ),
  );

  const balance = toNumber(
    firstDefined(
      document.BALANCE,
      document.OUTSTANDING_AMOUNT,
      document.OUTSTANDING,
      document.REMAINING_AMOUNT,
      document.balance,
      document.outstandingAmount,
      document.remainingAmount,
      total - paid,
    ),
  );

  const statusCode = firstDefined(
    document.STATUS_CODE,
    document.STATUS,
    document.statusCode,
    document.status,
    "",
  );

  const statusTitle = firstDefined(
    document.STATUS_TITLE,
    document.STATUS_NAME,
    document.statusTitle,
    document.statusName,
    "",
  );

  const status = normalizeStatus(
    firstDefined(statusCode, statusTitle),
    type,
  );

  const number = firstDefined(
    document.INVOICE_NUMBER,
    document.PROFORMA_NUMBER,
    document.DOCUMENT_NUMBER,
    document.NUMBER,
    document.invoiceNumber,
    document.proformaNumber,
    document.documentNumber,
    document.number,
    "—",
  );

  const issueDate = firstDefined(
    document.INVOICE_DATE,
    document.PROFORMA_DATE,
    document.DOCUMENT_DATE,
    document.ISSUE_DATE,
    document.invoiceDate,
    document.proformaDate,
    document.documentDate,
    document.issueDate,
    document.date,
    null,
  );

  const dueDate = firstDefined(
    document.DUE_DATE,
    document.EXPIRATION_DATE,
    document.dueDate,
    document.expirationDate,
    null,
  );

  const currency = firstDefined(
    document.CODE,
    document.CURRENCY,
    document.CURRENCY_CODE,
    document.currency,
    document.currencyCode,
    "USD",
  );

  return {
    ...document,

    id: firstDefined(
      document.INVOICE_ID,
      document.PROFORMA_ID,
      document.DOCUMENT_ID,
      document.ID,
      document.invoiceId,
      document.proformaId,
      document.documentId,
      document.id,
      null,
    ),

    number,
    title: number,

    type,

    description: firstDefined(
      document.DESCRIPTION,
      document.INVOICE_DESCRIPTION,
      document.PROFORMA_DESCRIPTION,
      document.TITLE,
      document.description,
      document.title,
      type === "proforma" ? "Proforma" : "Invoice",
    ),

    issueDate,
    dueDate,

    subtotal,
    discountAmount,
    discountPercent,

    tax: taxAmount,
    taxAmount,
    taxPercent,

    total,
    paid,
    balance,

    currency,
    currencyId: firstDefined(
      document.LT_CURRENCY_ID,
      document.CURRENCY_ID,
      document.currencyId,
      document.currency_id,
      null,
    ),

    status,
    statusCode,
    statusTitle,

    items,
    itemsCount: items.length,

    partitionId: firstDefined(
      document.PARTITION_ID,
      document.partitionId,
      null,
    ),

    companyId: firstDefined(
      document.COMPANY_ID,
      document.companyId,
      null,
    ),

    licenseId: firstDefined(
      document.PARTITION_LICENSES_ID,
      document.LICENSE_ID,
      document.licenseId,
      null,
    ),

    licenseNumber: firstDefined(
      document.LICENSE_NUMBER,
      document.licenseNumber,
      null,
    ),

    createdAt: firstDefined(
      document.CREATE_DATE,
      document.CREATED_AT,
      document.createDate,
      document.createdAt,
      null,
    ),

    createdBy: firstDefined(
      document.CREATE_USER_ID,
      document.CREATED_BY,
      document.createUserId,
      document.createdBy,
      null,
    ),

    raw: document,
  };
};

// -----------------------------------------------------------------------------
// DOCUMENT NORMALIZERS
// -----------------------------------------------------------------------------

function normalizeInvoice(invoice = {}) {
  return normalizeDocument(invoice, "invoice");
}

function normalizeInvoiceDetail(
  document = {},
  forcedType = "invoice",
) {
  return normalizeDocument(document, forcedType);
}

function normalizeProforma(proforma = {}) {
  return normalizeDocument(proforma, "proforma");
}

// -----------------------------------------------------------------------------
// FINANCIAL SUMMARY
// -----------------------------------------------------------------------------

function normalizeSummary(summary = {}) {
  const thisMonth = firstDefined(
    summary.THIS_MONTH,
    summary.thisMonth,
    {},
  );

  return {
    totalInvoiced: toNumber(
      firstDefined(
        summary.TOTAL_INVOICED,
        summary.totalInvoiced,
        0,
      ),
    ),

    totalPaid: toNumber(
      firstDefined(
        summary.TOTAL_PAID,
        summary.totalPaid,
        0,
      ),
    ),

    totalOutstanding: toNumber(
      firstDefined(
        summary.TOTAL_OUTSTANDING,
        summary.totalOutstanding,
        0,
      ),
    ),

    totalProforma: toNumber(
      firstDefined(
        summary.TOTAL_PROFORMA,
        summary.totalProforma,
        0,
      ),
    ),

    overdue: toNumber(
      firstDefined(
        summary.OVERDUE,
        summary.TOTAL_OVERDUE,
        summary.overdue,
        0,
      ),
    ),

    thisMonth: {
      invoiced: toNumber(
        firstDefined(
          thisMonth.INVOICED,
          thisMonth.invoiced,
          0,
        ),
      ),

      paid: toNumber(
        firstDefined(
          thisMonth.PAID,
          thisMonth.paid,
          0,
        ),
      ),

      outstanding: toNumber(
        firstDefined(
          thisMonth.OUTSTANDING,
          thisMonth.outstanding,
          0,
        ),
      ),
    },
  };
}

// -----------------------------------------------------------------------------
// FINANCIAL REPORT
// -----------------------------------------------------------------------------

function normalizeFinancialReportRow(row = {}) {
  const type = normalizeDocumentType(
    firstDefined(
      row.DOCUMENT_TYPE,
      row.TYPE,
      row.FILTER_TYPE,
      row.documentType,
      row.type,
      "invoice",
    ),
    "invoice",
  );

  const statusCode = firstDefined(
    row.STATUS,
    row.STATUS_CODE,
    row.status,
    row.statusCode,
    "",
  );

  const statusTitle = firstDefined(
    row.STATUS_TITLE,
    row.STATUS_NAME,
    row.statusTitle,
    row.statusName,
    "",
  );

  const debit = toNumber(
    firstDefined(
      row.DEBIT,
      row.DEBIT_AMOUNT,
      row.debit,
      row.debitAmount,
      0,
    ),
  );

  const credit = toNumber(
    firstDefined(
      row.CREDIT,
      row.CREDIT_AMOUNT,
      row.credit,
      row.creditAmount,
      0,
    ),
  );

  return {
    date: firstDefined(
      row.DOCUMENT_DATE,
      row.DATE,
      row.TRANSACTION_DATE,
      row.documentDate,
      row.transactionDate,
      row.date,
      "—",
    ),

    document: firstDefined(
      row.DOCUMENT_NUMBER,
      row.INVOICE_NUMBER,
      row.PROFORMA_NUMBER,
      row.DOCUMENT,
      row.documentNumber,
      row.invoiceNumber,
      row.proformaNumber,
      row.document,
      "—",
    ),

    type,

    description: firstDefined(
      row.DESCRIPTION,
      row.DOCUMENT_TITLE,
      row.DOCUMENT_TYPE,
      row.FILTER_TYPE,
      row.description,
      row.documentTitle,
      "—",
    ),

    debit,
    credit,

    amount: toNumber(
      firstDefined(
        row.AMOUNT,
        row.TOTAL_AMOUNT,
        row.amount,
        debit || credit,
        0,
      ),
    ),

    status: normalizeStatus(
      firstDefined(statusCode, statusTitle),
      type,
    ),

    statusCode,
    statusTitle,

    currency: firstDefined(
      row.CODE,
      row.CURRENCY,
      row.CURRENCY_CODE,
      row.currency,
      row.currencyCode,
      "USD",
    ),

    raw: row,
  };
}

// -----------------------------------------------------------------------------
// INVOICES
// -----------------------------------------------------------------------------

export async function fetchInvoices({
  statusCode = null,
} = {}) {
  try {
    const response = await api.post(
      "/DS/admin_center/get_partition_invoices.zjs",
      {
        status_code: statusCode,
      },
    );

    const decoded = decodeApiResponse(response);
    const result = getArrayResult(decoded);

    return result.map(normalizeInvoice);
  } catch (error) {
    console.error("Failed to fetch invoices:", error);
    throw error;
  }
}

// -----------------------------------------------------------------------------
// INVOICE DETAIL
// -----------------------------------------------------------------------------

export async function fetchInvoiceDetail(invoiceId) {
  try {
    const response = await api.post(
      "/DS/admin_center/get_invoice_detail.zjs",
      {
        invoice_id: invoiceId,
      },
    );

    const decoded = decodeApiResponse(response);
    const result = getResult(decoded);

    if (!result || Array.isArray(result)) {
      return null;
    }

    return normalizeInvoiceDetail(result, "invoice");
  } catch (error) {
    console.error(
      `Failed to fetch invoice detail for ${invoiceId}:`,
      error,
    );

    throw error;
  }
}

// -----------------------------------------------------------------------------
// PROFORMAS
// -----------------------------------------------------------------------------

export async function fetchProformas() {
  try {
    const response = await api.post(
      "/DS/admin_center/get_proformas.zjs",
    );

    const decoded = decodeApiResponse(response);
    const result = getArrayResult(decoded);

    // مهم:
    // نوع سند به‌صورت اجباری proforma تنظیم می‌شود
    // تا فیلترهای BillingPayments.jsx آن را شناسایی کنند.
    return result.map((item) =>
      normalizeProforma(item),
    );
  } catch (error) {
    console.error("Failed to fetch proformas:", error);
    throw error;
  }
}

// -----------------------------------------------------------------------------
// FINANCIAL SUMMARY
// -----------------------------------------------------------------------------

export async function fetchFinancialSummary({
  ltCurrencyId = null,
} = {}) {
  try {
    const response = await api.post(
      "/DS/admin_center/get_financial_summary.zjs",
      {
        lt_currency_id: ltCurrencyId,
      },
    );

    const decoded = decodeApiResponse(response);

    const result =
      decoded?.result ??
      decoded?.RESULT ??
      decoded?.data ??
      decoded?.DATA ??
      {};

    return normalizeSummary(result);
  } catch (error) {
    console.error(
      "Failed to fetch financial summary:",
      error,
    );

    throw error;
  }
}

// -----------------------------------------------------------------------------
// FINANCIAL REPORT
// -----------------------------------------------------------------------------

export async function fetchFinancialReport({
  from = null,
  to = null,
  type = "ALL",
  ltCurrencyId = null,
} = {}) {
  try {
    if (
      ltCurrencyId === null ||
      ltCurrencyId === undefined ||
      ltCurrencyId === ""
    ) {
      throw new Error(
        "lt_currency_id is required for financial report",
      );
    }

    const payload = {
      from_date: from,
      to_date: to,
      document_type: type?.toUpperCase() || "ALL",
      lt_currency_id: ltCurrencyId,
      // "from_date": "2026-07-01",
      // "to_date": "2026-08-31",
      // "document_type": "ALL",
      // "lt_currency_id": "101"

    };

    console.log("Financial report payload:", payload);

    const response = await api.post(
      "/DS/admin_center/get_financial_report.zjs",
      payload,
    );

    const decoded = decodeApiResponse(response);
    const result = getResult(decoded);

    let rawRows = [];

    if (Array.isArray(result)) {
      rawRows = result;
    } else if (result && typeof result === "object") {
      rawRows =
        result.rows ??
        result.ROWS ??
        result.data ??
        result.DATA ??
        result.items ??
        result.ITEMS ??
        result.results ??
        result.RESULTS ??
        [];
    }

    const rows = Array.isArray(rawRows)
      ? rawRows
        .filter(Boolean)
        .map(normalizeFinancialReportRow)
      : [];

    const total = toNumber(
      firstDefined(
        decoded?.total,
        decoded?.TOTAL,
        decoded?.count,
        decoded?.COUNT,
        result?.total,
        result?.TOTAL,
        rows.length,
      ),
      rows.length,
    );

    return {
      rows,
      total,
    };
  } catch (error) {
    console.error("Failed to fetch financial report:", error);
    throw error;
  }
}


// -----------------------------------------------------------------------------
// PENDING PAYMENTS
// -----------------------------------------------------------------------------

export async function fetchPendingPayments() {
  try {
    return await getPendingPayments();
  } catch (error) {
    console.error(
      "Failed to fetch pending payments:",
      error,
    );

    throw error;
  }
}

// -----------------------------------------------------------------------------
// INVOICE STATUS HELPERS
// -----------------------------------------------------------------------------

export async function fetchDraftInvoices() {
  return fetchInvoices({
    statusCode: "DRAFT",
  });
}

export async function fetchPendingInvoices() {
  return fetchInvoices({
    statusCode: "PENDING",
  });
}

export async function fetchPaidInvoices() {
  return fetchInvoices({
    statusCode: "PAID",
  });
}

export async function fetchCanceledInvoices() {
  return fetchInvoices({
    statusCode: "CANCELED",
  });
}

export async function fetchExpiredInvoices() {
  return fetchInvoices({
    statusCode: "EXPIRED",
  });
}

// -----------------------------------------------------------------------------
// DEFAULT EXPORT
// -----------------------------------------------------------------------------

const billingPaymentsService = {
  fetchInvoices,
  fetchInvoiceDetail,
  fetchProformas,
  fetchFinancialSummary,
  fetchFinancialReport,
  fetchPendingPayments,

  fetchDraftInvoices,
  fetchPendingInvoices,
  fetchPaidInvoices,
  fetchCanceledInvoices,
  fetchExpiredInvoices,
};

export default billingPaymentsService;
